<template>
  <div class="card mb-5 mb-xxl-8">
    <div v-if="isReady" class="card">
      <div class="card-header flex-wrap border-0 pt-6 pb-0">
        <div class="card-title flex-column align-items-start">
          <h3 class="card-label mb-1">{{ $t('credit.list_title') }}</h3>
          <span class="text-muted fs-7">{{ $t('credit.list_hint') }}</span>
        </div>
      </div>

      <div class="card-body pt-4">
        <!-- Totaux -->
        <div class="d-flex flex-wrap gap-4 mb-6">
          <div class="border border-gray-300 border-dashed rounded min-w-175px py-3 px-4">
            <div class="fs-2 fw-bolder text-danger">{{ money(summary.total_due) }}</div>
            <div class="fw-semibold fs-7 text-gray-500">{{ $t('credit.total_due') }}</div>
          </div>
          <div class="border border-gray-300 border-dashed rounded min-w-150px py-3 px-4">
            <div class="fs-2 fw-bolder text-gray-900">{{ summary.customers }}</div>
            <div class="fw-semibold fs-7 text-gray-500">{{ $t('credit.customers_count') }}</div>
          </div>
          <div class="border border-gray-300 border-dashed rounded min-w-175px py-3 px-4">
            <div class="fs-2 fw-bolder" :class="summary.overdue_total > 0 ? 'text-warning' : 'text-gray-900'">{{ money(summary.overdue_total) }}</div>
            <div class="fw-semibold fs-7 text-gray-500">{{ $t('credit.overdue_total', { count: summary.overdue_count }) }}</div>
          </div>
        </div>

        <!-- Filtres -->
        <div class="d-flex flex-wrap align-items-center gap-4 mb-5">
          <input
              v-model="search"
              type="text"
              class="form-control form-control-solid w-250px"
              :placeholder="$t('credit.search')"
              @input="debouncedLoad"
          />
          <label class="form-check form-check-custom form-check-solid">
            <input v-model="overdueOnly" class="form-check-input" type="checkbox" @change="load" />
            <span class="form-check-label">{{ $t('credit.overdue_only') }}</span>
          </label>
        </div>

        <div v-if="customers.length === 0" class="text-muted text-center py-10">
          {{ $t('credit.empty') }}
        </div>

        <div v-else class="table-responsive">
          <table class="table align-middle table-row-bordered fs-6 gy-3">
            <thead>
              <tr class="text-start text-gray-400 fw-bolder fs-7 text-uppercase gs-0">
                <th class="min-w-175px">{{ $t('credit.customer') }}</th>
                <th class="min-w-100px">{{ $t('credit.sales_count') }}</th>
                <th class="min-w-125px">{{ $t('credit.next_due') }}</th>
                <th class="min-w-125px text-end">{{ $t('credit.due') }}</th>
                <th class="w-50px"></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="row in customers" :key="row.customer ? row.customer.id : 'none'">
                <tr class="cursor-pointer" @click="toggle(row)">
                  <td>
                    <div class="fw-bold text-gray-900">{{ row.customer ? row.customer.name : '—' }}</div>
                    <div v-if="row.customer && row.customer.phone" class="text-muted fs-7">{{ row.customer.phone }}</div>
                  </td>
                  <td>{{ row.count }}</td>
                  <td>
                    <span v-if="row.next_due_date" :class="isOverdue(row.next_due_date) ? 'badge badge-light-danger' : 'text-gray-700'">
                      {{ formatDate(row.next_due_date) }}
                    </span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td class="text-end fw-bolder text-danger">{{ money(row.total_due) }}</td>
                  <td class="text-end">
                    <i class="bi" :class="isOpen(row) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                  </td>
                </tr>
                <tr v-if="isOpen(row)" class="bg-light">
                  <td colspan="5" class="p-0">
                    <table class="table table-sm align-middle mb-0 fs-7">
                      <thead>
                        <tr class="text-gray-500 fw-bold">
                          <th class="ps-6">{{ $t('credit.sale') }}</th>
                          <th>{{ $t('credit.date') }}</th>
                          <th class="text-end">{{ $t('credit.total') }}</th>
                          <th class="text-end">{{ $t('credit.paid') }}</th>
                          <th class="text-end">{{ $t('credit.rest') }}</th>
                          <th>{{ $t('credit.due_date_short') }}</th>
                          <th class="pe-6"></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="sale in row.credits" :key="sale.id">
                          <td class="ps-6">
                            <router-link :to="{ name: 'order-details', params: { id: route.params.id, detail_id: sale.id } }" class="fw-bold">
                              {{ sale.reference }}
                            </router-link>
                          </td>
                          <td>{{ formatDate(sale.date) }}</td>
                          <td class="text-end">{{ money(sale.total) }}</td>
                          <td class="text-end">{{ money(sale.paid) }}</td>
                          <td class="text-end fw-bold text-danger">{{ money(sale.rest) }}</td>
                          <td>
                            <span v-if="sale.due_date" :class="sale.overdue ? 'badge badge-light-danger' : ''">{{ formatDate(sale.due_date) }}</span>
                            <span v-else class="text-muted">—</span>
                          </td>
                          <td class="pe-6 text-end">
                            <button
                                v-if="can('credit_payment')"
                                type="button"
                                class="btn btn-sm btn-light-success py-1"
                                @click.stop="openPayment(sale, row)"
                            >
                              {{ $t('credit.collect') }}
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div v-else class="card">
      <Loader />
    </div>

    <!-- Encaisser un règlement -->
    <template v-if="payment.sale">
      <div class="modal fade show d-block" tabindex="-1" @click.self="closePayment">
        <div class="modal-dialog modal-dialog-centered modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ $t('credit.collect_title') }}</h5>
              <button type="button" class="btn btn-icon btn-sm btn-active-light-primary" @click="closePayment">
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
            <div class="modal-body">
              <div class="d-flex justify-content-between mb-4 p-3 rounded bg-light">
                <div>
                  <div class="fw-bold">{{ payment.customer }}</div>
                  <div class="text-muted fs-7">{{ payment.sale.reference }}</div>
                </div>
                <div class="text-end">
                  <div class="text-muted fs-7">{{ $t('credit.rest') }}</div>
                  <div class="fw-bolder text-danger fs-4">{{ money(payment.sale.rest) }}</div>
                </div>
              </div>

              <!-- Historique des règlements de la vente -->
              <div class="mb-5">
                <div class="fw-bold fs-6 mb-2">{{ $t('credit.history') }}</div>
                <div v-if="!payment.sale.payments || !payment.sale.payments.length" class="text-muted fs-7">
                  {{ $t('credit.no_payment') }}
                </div>
                <table v-else class="table table-sm table-row-dashed align-middle fs-7 mb-0">
                  <thead>
                    <tr class="text-gray-500 fw-bold">
                      <th>{{ $t('credit.date') }}</th>
                      <th>{{ $t('credit.mode') }}</th>
                      <th>{{ $t('credit.by') }}</th>
                      <th class="text-end">{{ $t('credit.amount_short') }}</th>
                      <th class="text-end">{{ $t('credit.rest') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="p in payment.sale.payments" :key="p.id">
                      <td>
                        {{ formatDateTime(p.date) }}
                      </td>
                      <td>{{ modeLabel(p.payment_method) }}</td>
                      <td>{{ p.user || '—' }}</td>
                      <td class="text-end fw-bold text-success">{{ money(p.amount) }}</td>
                      <td class="text-end">{{ money(p.rest) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <label class="form-label required">{{ $t('credit.amount') }}</label>
              <div class="input-group mb-2">
                <input v-model.number="payment.amount" type="number" min="1" :max="payment.sale.rest" class="form-control" />
                <span class="input-group-text">{{ currency }}</span>
              </div>
              <div class="mb-4">
                <button type="button" class="btn btn-link btn-sm p-0" @click="payment.amount = payment.sale.rest">
                  {{ $t('credit.pay_all') }}
                </button>
              </div>

              <label class="form-label">{{ $t('credit.mode') }}</label>
              <div class="d-flex flex-wrap gap-2 mb-4">
                <button
                    v-for="mode in modes"
                    :key="mode.value"
                    type="button"
                    class="btn btn-sm"
                    :class="payment.mode === mode.value ? 'btn-primary' : 'btn-light'"
                    @click="payment.mode = mode.value"
                >{{ mode.label }}</button>
              </div>

              <label class="form-label">{{ $t('credit.note') }}</label>
              <textarea v-model="payment.note" class="form-control" rows="2" maxlength="1000"></textarea>

              <div v-if="payment.error" class="text-danger fs-7 mt-3">{{ payment.error }}</div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-light" @click="closePayment">{{ $t('cancel') }}</button>
              <button type="button" class="btn btn-success" :disabled="payment.saving || !validAmount" @click="submitPayment">
                <span v-if="payment.saving" class="spinner-border spinner-border-sm me-2"></span>
                {{ $t('credit.collect') }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade show"></div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import Loader from '@/components/Loader.vue'
import { useHasAbility } from '@/composables/hasAbility'
import { paymentModeLabel } from '@/helpers/paymentMode'

/**
 * Créances clients de la boutique : ventes en caisse à crédit ou à acompte non soldées,
 * regroupées par client, avec encaissement d'un règlement.
 */
const store = useStore()
const route = useRoute()
const t = (key, params) => window.i18n.global.t(key, params)
const { can } = useHasAbility()

const isReady = ref(false)
const customers = ref([])
const summary = ref({ total_due: 0, customers: 0, overdue_total: 0, overdue_count: 0 })
const search = ref('')
const overdueOnly = ref(false)
const opened = ref({})
const currency = ref('')

const modes = computed(() => [
  { value: 'liquidity', label: t('payment.cash') },
  { value: 'MOBILE', label: 'Mobile Money' },
  { value: 'MOMO_PAY', label: 'MoMo Pay' },
])

const emptyPayment = () => ({ sale: null, customer: '', amount: 0, mode: 'liquidity', note: '', saving: false, error: '' })
const payment = ref(emptyPayment())

const validAmount = computed(() => payment.value.amount > 0 && payment.value.amount <= payment.value.sale?.rest)

function money(value) {
  const amount = (Number(value) || 0).toLocaleString(window.i18n.global.locale || 'fr', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
  return amount + ' ' + currency.value
}

function formatDate(value) {
  return value ? new Date(value).toLocaleDateString('fr-FR') : ''
}

function formatDateTime(value) {
  return value ? new Date(value).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''
}

const modeLabel = paymentModeLabel

function isOverdue(date) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return new Date(date) < today
}

const keyOf = (row) => (row.customer ? row.customer.id : 'none')
const isOpen = (row) => !!opened.value[keyOf(row)]
function toggle(row) {
  opened.value = { ...opened.value, [keyOf(row)]: !isOpen(row) }
}

async function load() {
  const response = await window.axios.get(`get/shop/${route.params.id}/credits`, {
    params: { search: search.value || undefined, overdue: overdueOnly.value ? 1 : undefined },
  })
  customers.value = response.data.customers
  summary.value = response.data.summary
}

let timer = null
function debouncedLoad() {
  clearTimeout(timer)
  timer = setTimeout(load, 350)
}

function openPayment(sale, row) {
  payment.value = { ...emptyPayment(), sale, customer: row.customer ? row.customer.name : '', amount: sale.rest }
}

function closePayment() {
  payment.value = emptyPayment()
}

async function submitPayment() {
  payment.value.saving = true
  payment.value.error = ''
  try {
    await window.axios.post(`shop/${route.params.id}/credits/payments`, {
      pos_checkout_id: payment.value.sale.id,
      type: 'amount',
      payments: [{ mode: payment.value.mode, amount: payment.value.amount }],
      note: payment.value.note || null,
    }, { silent: true })
    window.toastr['success'](t('credit.collected'))
    const saleId = payment.value.sale.id
    await load()
    // fenêtre gardée ouverte avec l'historique à jour ; fermée quand la vente est soldée
    const updated = customers.value.flatMap((row) => row.credits).find((sale) => sale.id === saleId)
    if (updated) {
      payment.value = { ...payment.value, sale: updated, amount: updated.rest, note: '', saving: false }
    } else {
      closePayment()
    }
  } catch (e) {
    payment.value.error = e.response?.data?.message || t('credit.collect_error')
    payment.value.saving = false
  }
}

async function loadCurrency() {
  const response = await store.dispatch('shop/getCurrency')
  currency.value = response.data.currency_symbol || response.data.symbol_native || ''
}

async function init() {
  isReady.value = false
  try {
    await Promise.all([load(), loadCurrency()])
  } finally {
    isReady.value = true
  }
}

watch(() => route.params.id, (id) => id && init())
onMounted(init)
</script>
